import React, { useState } from "react";
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import Facebook from "../../../assets/icons/fb.svg";
import Twitter from "../../../assets/icons/twitter.svg";
import Instagram from "../../../assets/icons/insta.svg";
import LinkedIn from "../../../assets/icons/linkedin-new.svg";
import Logo from "../../../assets/icons/afp-logo.png";
import "./Footer.scss";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FACEBOOK_LINK,
  TWITTER_LINK,
  INSTAGRAM_LINK,
  LINKEDIN_LINK,
  AFP_HORIZON_WEBSITE,
  AFP_HORIZON_PRIVACY_POLICY_LINK,
} from "../../../utils/constants";
import { subscribeNewsLetter } from "../../../api/homepage.api";
import ToastMessage from "../toast-message/toast-message";

function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "info" | "warning" | "error",
  });

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleSubscribe = async () => {
    setIsSubmitted(true);
    if (!email) {
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      return;
    }

    setLoading(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await subscribeNewsLetter({ email, timezone });

      setToast({
        open: true,
        message: response.data?.data?.message || response.data?.message || "Subscribed successfully!",
        severity: "success",
      });
      setEmail("");
      setIsSubmitted(false);
    } catch (error: any) {
      setToast({
        open: true,
        message:
          error.response?.data?.message ||
          "Something went wrong. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLink = (path: string) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(path);
    }
  };

  return (
    <footer className="footer">
      <Container
        sx={{
          maxWidth: { md: "1390px" },
        }}
      >
        <Grid container alignItems="flex-start" className="footer__top" spacing={{ xs: 3, md: 0 }}>
          {/* Logo & About */}
          <Grid item xs={12} md={3}>
            <Box
              className="footer__logo-wrapper"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: { xs: "center", lg: "flex-start" },
              }}
            >
              <img src={Logo} alt="afp-logo" className="footer__logo-img" />
              <Typography variant="body2" className="footer__about">
                Since 1997, AFP has been a trusted partner to funeral homes,
                dedicated to enhancing your success.
              </Typography>
            </Box>

            <Box
              className="footer__socials"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", lg: "flex-start" },
                gap: "0.5rem",
                marginTop: "1rem",
                flexDirection: { xs: "column", sm: "row" },
                textAlign: { xs: "center", sm: "left" },
              }}
            >
              <Typography
                variant="body2"
                className="footer__follow"
                sx={{
                  marginTop: "0.5rem",
                }}
              >
                FOLLOW US
              </Typography>
              <Box className="footer__icons">
                <a
                  href={FACEBOOK_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={Facebook} alt="Facebook" />
                </a>
                <a
                  href={TWITTER_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={Twitter} alt="Twitter" />
                </a>
                <a
                  href={INSTAGRAM_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={Instagram} alt="Instagram" />
                </a>
                <a
                  href={LINKEDIN_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={LinkedIn} alt="LinkedIn" />
                </a>
              </Box>
            </Box>
          </Grid>

          {/* Cols 2-4 */}
          <Grid item xs={12} md={5.5}>
            <Box className="footer__middle-cols">
              {/* Company */}
              <Box>
                <Typography variant="h6" className="footer__heading">
                  Company
                </Typography>
                <ul className="footer__list">
                  <li>
                    <a
                      href={AFP_HORIZON_WEBSITE}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer__link"
                    >
                      AFP Website
                    </a>
                  </li>
                  {/* <li>Blogs</li> */}
                </ul>
              </Box>

              {/* Quick Links */}
              <Box>
                <Typography variant="h6" className="footer__heading">
                  Quick Links
                </Typography>
                <ul className="footer__list">
                  {/* changed */}
                  <li onClick={() => handleQuickLink("/home")}>Home</li>
                  <li
                    onClick={() => {
                      if (location.pathname !== "/home") {
                        navigate("/home#why-memorialgo");
                      } else {
                        const el = document.getElementById("why-memorialgo");
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                  >
                    <span
                      className="footer__link"
                      style={{ cursor: "pointer" }}
                    >
                      Why MemorialGo
                    </span>
                  </li>
                  <li onClick={() => handleQuickLink("/services")}>Services</li>
                  <li onClick={() => handleQuickLink("/contact-us")}>
                    Contact Us
                  </li>
                </ul>
              </Box>

              {/* Legal */}
              <Box>
                <Typography variant="h6" className="footer__heading">
                  Legal
                </Typography>
                <ul className="footer__list">
                  <li>
                    <a
                      href={AFP_HORIZON_PRIVACY_POLICY_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer__link"
                    >
                      Privacy Policy
                    </a>
                  </li>
                  {/* <li>Terms & Conditions</li> */}
                  <li>
                    <a
                      href="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer__link"
                    >
                      Terms of Use
                    </a>
                  </li>
                </ul>
              </Box>
            </Box>
          </Grid>

          {/* Newsletter */}
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              marginLeft: { md: "1.8rem" },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography variant="h6" className="footer__heading">
              Join our Newsletter
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Your email address"
              size="small"
              className="footer__input"
              value={email}
              error={isSubmitted && (!email || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email))}
              helperText={isSubmitted && (!email ? "Please enter an email address" : !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email) ? "Please enter a valid email address" : "")}
              onChange={(e) => {
                if (e.target.value.length <= 50) {
                  setEmail(e.target.value);
                }
              }}
              inputProps={{ maxLength: 50 }}
              type="email"
            />
            <Button
              variant="contained"
              className="footer__btn"
              onClick={handleSubscribe}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Subscribe"
              )}
            </Button>
            <Typography variant="caption" className="footer__note">
              * Will send you weekly updates for your better tool management.
            </Typography>
          </Grid>
        </Grid>

        {/* Bottom copyright */}
        <Box className="footer__bottom">
          <Typography variant="body2">
            © {new Date().getFullYear()} - All rights reserved
          </Typography>
        </Box>
      </Container>
      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </footer>
  );
}

export default Footer;
